'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Check, ChevronRight, ChevronLeft, Building2, User, Phone, Mail, MapPin, Loader2, CheckCircle2, Shield, Zap } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface LabRegForm {
  labName: string;
  vatNumber: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  plan: string;
}

type Step = 'lab' | 'admin' | 'confirm' | 'success';

const STEPS: { id: Step; label: string }[] = [
  { id: 'lab', label: 'Laboratorio' },
  { id: 'admin', label: 'Account' },
  { id: 'confirm', label: 'Conferma' },
];

export default function RegisterLabPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>('lab');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<LabRegForm>({
    labName: '',
    vatNumber: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    plan: 'pro',
  });

  const update = (field: keyof LabRegForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const stepIndex = STEPS.findIndex(s => s.id === step);

  const handleNext = () => {
    if (step === 'lab') {
      if (!form.labName || !form.email) { setError('Nome laboratorio e email sono obbligatori.'); return; }
      setStep('admin');
    } else if (step === 'admin') {
      if (!form.adminName || !form.adminEmail || !form.adminPassword) { setError('Tutti i campi account sono obbligatori.'); return; }
      if (form.adminPassword.length < 8) { setError('La password deve essere di almeno 8 caratteri.'); return; }
      setStep('confirm');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/superadmin/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Errore durante la registrazione.');
      setStep('success');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">Laboratorio registrato!</h1>
          <p className="text-muted-foreground mb-2">
            Il laboratorio <strong>{form.labName}</strong> è in fase di configurazione.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Riceverai un'email a <strong>{form.adminEmail}</strong> con le credenziali di accesso.
          </p>
          <div className="bg-muted rounded-xl p-4 mb-8 text-left">
            <p className="text-xs font-medium text-muted-foreground mb-2">CONFIGURAZIONE IN CORSO</p>
            <ul className="space-y-2">
              {['Database isolato', 'Numero Twilio dedicato', 'Workspace Cal.com', 'Account amministratore', 'Email di benvenuto'].map(item => (
                <li key={item} className="flex items-center gap-2 text-sm text-foreground">
                  <Check className="w-4 h-4 text-green-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <Link href="/login" className="block w-full bg-primary text-white rounded-xl py-3 font-semibold hover:bg-primary/90 transition-colors text-center">
            Vai al Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-border px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-semibold text-lg text-foreground">LabCall<span className="text-primary">Manager</span></span>
          </Link>
          <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Hai già un account? <span className="text-primary font-medium">Accedi</span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Step indicator */}
        <div className="flex items-center justify-center mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  i < stepIndex ? 'bg-green-500 text-white' :
                  i === stepIndex ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {i < stepIndex ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${i === stepIndex ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-px w-8 sm:w-16 mx-2 transition-colors ${i < stepIndex ? 'bg-green-500' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step: Lab Details */}
        {step === 'lab' && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">Registra il tuo Laboratorio</h1>
              <p className="text-muted-foreground">Inserisci i dati del laboratorio per iniziare.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-border p-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Nome Laboratorio *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="text" value={form.labName} onChange={e => update('labName', e.target.value)} placeholder="es. Laboratorio Rossi" className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Laboratorio *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="info@laboratorio.it" className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Telefono</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+39 02 1234567" className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">P.IVA</label>
                  <input type="text" value={form.vatNumber} onChange={e => update('vatNumber', e.target.value)} placeholder="IT12345678901" className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Indirizzo</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="text" value={form.address} onChange={e => update('address', e.target.value)} placeholder="Via Roma 1" className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Città</label>
                <input type="text" value={form.city} onChange={e => update('city', e.target.value)} placeholder="Milano" className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
              <button onClick={handleNext} className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                Continua <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step: Admin Account */}
        {step === 'admin' && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">Account Amministratore</h1>
              <p className="text-muted-foreground">Crea l'account per gestire il laboratorio.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-border p-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Nome Completo *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="text" value={form.adminName} onChange={e => update('adminName', e.target.value)} placeholder="Mario Rossi" className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Amministratore *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="email" value={form.adminEmail} onChange={e => update('adminEmail', e.target.value)} placeholder="admin@laboratorio.it" className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password *</label>
                <input type="password" value={form.adminPassword} onChange={e => update('adminPassword', e.target.value)} placeholder="Minimo 8 caratteri" className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setStep('lab')} className="flex items-center gap-2 px-5 py-3 border border-border rounded-xl text-sm font-medium hover:bg-muted transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Indietro
                </button>
                <button onClick={handleNext} className="flex-1 flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                  Continua <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step: Confirm */}
        {step === 'confirm' && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">Conferma Registrazione</h1>
              <p className="text-muted-foreground">Verifica i dati prima di procedere.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-border p-8 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/30 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Laboratorio</p>
                  <p className="font-semibold text-foreground">{form.labName}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Email Lab</p>
                  <p className="font-semibold text-foreground text-sm">{form.email}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Amministratore</p>
                  <p className="font-semibold text-foreground">{form.adminName}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Email Admin</p>
                  <p className="font-semibold text-foreground text-sm">{form.adminEmail}</p>
                </div>
                {form.city && (
                  <div className="p-4 bg-muted/30 rounded-xl">
                    <p className="text-xs text-muted-foreground mb-1">Città</p>
                    <p className="font-semibold text-foreground">{form.city}</p>
                  </div>
                )}
                {form.vatNumber && (
                  <div className="p-4 bg-muted/30 rounded-xl">
                    <p className="text-xs text-muted-foreground mb-1">P.IVA</p>
                    <p className="font-semibold text-foreground">{form.vatNumber}</p>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <Shield className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Dati protetti</p>
                  <p className="text-xs text-blue-600 mt-0.5">I tuoi dati sono crittografati e protetti secondo il GDPR.</p>
                </div>
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setStep('admin')} className="flex items-center gap-2 px-5 py-3 border border-border rounded-xl text-sm font-medium hover:bg-muted transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Indietro
                </button>
                <button onClick={handleSubmit} disabled={loading} className="flex-1 flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
                  {loading ? 'Registrazione...' : 'Registra Laboratorio'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
